import * as React from 'react';
import { useCallback, useState } from 'react';
import { Button, Container, FormField, Header, Input, SpaceBetween, Alert } from '@amzn/awsui-components-react';
import CameraModal from '../cameraModal';
import decodeImage from '../camera/imageDecoder';
import { ComponentType } from './componentType';

interface ComponentInstallProps {
    deviceId: string;
    vin: string;
    componentType: ComponentType;
    onInstallComponent: (serialNumber: string) => void;
    onCancel: () => void;
    isSubmitting?: boolean;
}

const ComponentInstall: React.FC<ComponentInstallProps> = React.memo(
    ({ deviceId, vin, componentType, onInstallComponent, onCancel, isSubmitting = false }) => {
        const [serialNumber, setSerialNumber] = useState('');
        const [errors, setErrors] = useState<Record<string, string>>({});
        const [showCameraModal, setShowCameraModal] = useState(false);
        const [serialNumberCameraLoading, setSerialNumberCameraLoading] = useState(false);
        const [isSerialNumberValid, setIsSerialNumberValid] = useState(false);

        const validateSerialNumber = useCallback((serialNumber: string) => {
            const trimmedSerialNumber = serialNumber.trim();
            const isValid = Boolean(trimmedSerialNumber && trimmedSerialNumber.length === 16);

            if (!trimmedSerialNumber || trimmedSerialNumber.length <= 0) {
                setErrors((prev) => ({ ...prev, serialNumber: 'Component serial number is required' }));
            } else if (trimmedSerialNumber.length !== 16) {
                setErrors((prev) => ({
                    ...prev,
                    serialNumber: 'Component serial number should be 16 characters in length',
                }));
            } else {
                setErrors((prev) => ({ ...prev, serialNumber: '' }));
            }

            setIsSerialNumberValid(isValid);
            return isValid;
        }, []);

        const handleSerialNumberChange = useCallback(
            (e: any) => {
                const newSerialNumber = e.detail.value.toUpperCase();
                setSerialNumber(newSerialNumber);
                validateSerialNumber(newSerialNumber);
            },
            [validateSerialNumber],
        );

        const setSerialNumberFromImage = useCallback(
            (img: any) => {
                setSerialNumberCameraLoading(true);

                decodeImage(img, (text?: string, error?: any) => {
                    if (text) {
                        const upperCaseText = text.toUpperCase();
                        setSerialNumber(upperCaseText);
                        validateSerialNumber(upperCaseText);
                    }
                    setSerialNumberCameraLoading(false);
                });
            },
            [validateSerialNumber],
        );

        const handleCameraCapture = useCallback(
            (scannedText: string) => {
                const upperCaseText = scannedText.toUpperCase();
                setSerialNumber(upperCaseText);
                validateSerialNumber(upperCaseText);
            },
            [validateSerialNumber],
        );

        const handleSubmit = useCallback(
            async (e: React.FormEvent) => {
                e.preventDefault();

                if (!isSerialNumberValid) {
                    return;
                }

                onInstallComponent(serialNumber);
            },
            [serialNumber, isSerialNumberValid, onInstallComponent],
        );

        return (
            <Container>
                <form onSubmit={handleSubmit}>
                    <SpaceBetween size="l">
                        <Header variant="h2">Install Component</Header>

                        <Alert type="success" header="Device Check Complete">
                            No existing {componentType.label} component found. You can proceed with the installation.
                        </Alert>

                        <FormField
                            label="Device ID"
                            description="The ID of the device you're installing the component on"
                        >
                            <div>{deviceId}</div>
                        </FormField>

                        <FormField label="VIN" description="Vehicle Identification Number">
                            <div>{vin}</div>
                        </FormField>

                        <FormField label="Component Type" description="The type of component being installed">
                            <div>{componentType.label}</div>
                        </FormField>

                        <FormField
                            label="Component Serial Number"
                            description="Enter or scan the component serial number"
                            errorText={errors.serialNumber}
                        >
                            <Input
                                value={serialNumber}
                                onChange={handleSerialNumberChange}
                                placeholder="Enter serial number..."
                                invalid={!isSerialNumberValid && serialNumber.length > 0}
                                disabled={isSubmitting}
                            />
                            <Button
                                variant="inline-icon"
                                iconName="video-on"
                                loading={serialNumberCameraLoading}
                                onClick={() => setShowCameraModal(true)}
                                disabled={isSubmitting}
                                ariaLabel="Scan serial number with camera"
                            />
                        </FormField>

                        {errors.form && <div style={{ color: 'red' }}>{errors.form}</div>}

                        {isSubmitting && (
                            <Alert type="info" header="Preparing for Installation">
                                Preparing the device for installing {componentType.label}... This could take a few
                                seconds.
                            </Alert>
                        )}

                        <SpaceBetween size="xs" direction="horizontal">
                            <Button variant="primary" loading={isSubmitting} formAction="submit">
                                Install Component
                            </Button>
                            <Button variant="link" onClick={onCancel} disabled={isSubmitting}>
                                Cancel
                            </Button>
                        </SpaceBetween>
                    </SpaceBetween>
                </form>

                <CameraModal
                    setUserImageCallback={setSerialNumberFromImage}
                    isValidTextOutput={validateSerialNumber}
                    setTextOutput={handleCameraCapture}
                    visible={showCameraModal}
                    setVisible={setShowCameraModal}
                    header="Take Picture of Component Serial Number"
                />
            </Container>
        );
    },
);

export default ComponentInstall;
